/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    COMPANY_TAB_COMP
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Extracted company tab content for ReviewProfileForm.
 *
 * File Size Check:
 * - Current: ~160 lines
 * - Limit: 200 lines
 * - Decision: New component creation (under limit)
 */
import React from "react";
import { Row, Col, Card, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import type { User, ImageData } from "../../../../interfaces/User";

interface CompanyTabProps {
  profile: Partial<User> | null;
  getImageSrc: (img?: ImageData | null) => string | null;
  companyAddress: string;
  hoursText: string;
}

export const CompanyTab: React.FC<CompanyTabProps> = ({
  profile,
  getImageSrc,
  companyAddress,
  hoursText,
}) => {
  const navigate = useNavigate();
  const logo =
    (profile as any)?.companyLogos && (profile as any).companyLogos.length
      ? (profile as any).companyLogos[0]
      : undefined;

  const handleEdit = () => {
    navigate("/setup?section=company");
  };

  return (
    <Row className="review-section" style={{ minWidth: 0 }}>
      <Col md={8} style={{ minWidth: 0 }}>
        <Card className="review-card mb-3" style={{ width: "100%", maxWidth: "100%" }}>
          <Card.Body>
            <div className="review-page-header d-flex justify-content-end mb-4">
              <Button
                variant="outline-secondary"
                className="review-edit-button"
                onClick={handleEdit}
                aria-label="Edit company profile"
              >
                <img src="/assets/editprofile.svg" alt="Edit" width={32} height={32} />
              </Button>
            </div>

            <div className="d-flex align-items-start justify-content-between mb-3">
              <div className="d-flex align-items-center gap-3">
                {logo && (
                  <div className="review-company-logo">
                    <img src={getImageSrc(logo) || ""} alt="Company logo" />
                  </div>
                )}
                <div>
                  <h4 className="mb-1">{profile?.companyName || "Company name not set"}</h4>
                  <div className="text-muted">{profile?.country || "Country not set"}</div>
                </div>
              </div>
            </div>

            <div className="review-section-block">
              <h6 className="review-section-title">About Company</h6>
              <p className="review-section-text">
                {(profile as any)?.aboutCompany ||
                  "You haven't added information about your company yet."}
              </p>
            </div>

            <div className="review-section-block">
              <h6 className="review-section-title">Our Services</h6>
              <p className="review-section-text">
                {profile?.services || "Describe the main services your company provides."}
              </p>
            </div>

            {Array.isArray((profile as any)?.certifications) &&
              (profile as any).certifications.length > 0 && (
                <div className="review-section-block">
                  <h6 className="review-section-title">Our Certifications</h6>
                  <div className="review-certifications">
                    {(profile as any).certifications.map((cert: ImageData, idx: number) => (
                      <div key={idx} className="review-certifications__item">
                        <img src={getImageSrc(cert) || ""} alt={`Certification ${idx + 1}`} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
          </Card.Body>
        </Card>
      </Col>

      <Col md={4} style={{ minWidth: 0 }}>
        <Card className="review-card mb-3" style={{ width: "100%", maxWidth: "100%" }}>
          <Card.Body>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="mb-0">Company Details</h6>
              <FontAwesomeIcon icon={faBuilding} className="text-muted" />
            </div>

            <dl className="review-details-list">
              <div className="review-details-row">
                <dt>Website</dt>
                <dd>
                  {profile?.website ? (
                    <a href={profile.website} target="_blank" rel="noopener noreferrer">
                      {profile.website}
                    </a>
                  ) : (
                    "Not provided"
                  )}
                </dd>
              </div>
              <div className="review-details-row">
                <dt>Email</dt>
                <dd>{profile?.companyEmail || "Not provided"}</dd>
              </div>
              <div className="review-details-row">
                <dt>Phone</dt>
                <dd>{profile?.telCompany || "Not provided"}</dd>
              </div>
              <div className="review-details-row">
                <dt>Address</dt>
                <dd>{companyAddress || "Not provided"}</dd>
              </div>
              <div className="review-details-row">
                <dt>Hours of operation</dt>
                <dd>{hoursText}</dd>
              </div>
            </dl>
          </Card.Body>
        </Card>

        <Card className="review-card">
          <Card.Body>
            <h6 className="mb-3">Social media</h6>
            <div className="review-social-list">
              {[
                { label: "LinkedIn", value: profile?.companyLinkedIn },
                { label: "Facebook", value: profile?.companyFacebook },
                { label: "X / Twitter", value: profile?.companyX },
                { label: "Instagram", value: profile?.companyInstagram },
              ].map((item, idx) => (
                <div key={idx} className="review-social-row">
                  <span className="review-social-label">{item.label}</span>
                  <span className="review-social-value">
                    {item.value ? (
                      <a href={item.value} target="_blank" rel="noopener noreferrer">
                        {item.value}
                      </a>
                    ) : (
                      "Not provided"
                    )}
                  </span>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};
